import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { complianceApi, RiskProfile as Profile } from '../../api/compliance';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';

const MIN_REASON = 20;

const RATING_VARIANT: Record<string, 'danger' | 'warning' | 'success'> = {
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

/**
 * A customer's risk rating, and why.
 *
 * ## The breakdown is the point, not the number
 *
 * "The system said high" is not a defensible answer to a CBN examiner, and a
 * customer disputing an EDD request is entitled to a coherent reason. The
 * engine records a per-factor contribution with its own rationale precisely so
 * this screen can show the working rather than a score.
 *
 * ## Floors are shown separately from points
 *
 * Some obligations are categorical rather than weighted — FATF R.12 requires
 * enhanced due diligence for a foreign PEP unconditionally, so that factor
 * sets the rating regardless of the arithmetic. Rendering it as just another
 * row of points would make the total look wrong.
 *
 * ## Override changes the conclusion, not the evidence
 *
 * The computed factors stay visible after an override so the two can be
 * compared. An examiner reading this wants to see what the system concluded
 * *and* what a human decided instead.
 */
export const RiskProfile: React.FC = () => {
  usePage('Risk Profile');
  const { subjectType = 'user', subjectId = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState<'low' | 'medium' | 'high' | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProfile(await complianceApi.riskProfile(subjectType, subjectId));
    } catch {
      toast.error('Could not load the risk profile.');
    } finally {
      setLoading(false);
    }
  }, [subjectType, subjectId, toast]);

  useEffect(() => { void load(); }, [load]);

  async function override() {
    if (!rating || reason.trim().length < MIN_REASON) return;
    setBusy(true);
    try {
      setProfile(
        await complianceApi.overrideRisk(subjectType, subjectId, rating, reason.trim()),
      );
      setRating('');
      setReason('');
      toast.success('Rating overridden.');
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Could not override the rating.';
      toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Spinner />;

  if (!profile) {
    return (
      <Card>
        <p className="text-sm text-gray-600">
          This subject has never been assessed. A rating appears once they
          complete customer due diligence or the periodic review runs.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center gap-3">
          <Badge variant={RATING_VARIANT[profile.rating]}>{profile.rating}</Badge>
          <span className="text-2xl font-semibold">{profile.score}</span>
          {profile.assessedBy === 'manual' && <Badge variant="info">Manually set</Badge>}
          {profile.isPep && <Badge variant="warning">PEP{profile.pepCategory ? ` · ${profile.pepCategory}` : ''}</Badge>}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
          <div>
            <div className="text-xs text-gray-500">Occupation</div>
            <div>{profile.occupation ?? <span className="text-gray-400">Not supplied</span>}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Expected volume</div>
            <div>
              {profile.expectedMonthlyVolume
                ? `₦${Number(profile.expectedMonthlyVolume).toLocaleString()}/mo`
                : <span className="text-gray-400">Not supplied</span>}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Last assessed</div>
            <div>{profile.lastAssessedAt ? new Date(profile.lastAssessedAt).toLocaleDateString() : '—'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Next review</div>
            <div>{profile.nextReviewAt ? new Date(profile.nextReviewAt).toLocaleDateString() : '—'}</div>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-semibold">
          Why this rating
        </h2>
        <div className="space-y-2">
          {profile.factors.map((f) => (
            <div key={f.factor} className="rounded border p-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs">{f.factor}</span>
                <span className="flex items-center gap-2">
                  {f.floor && (
                    // Categorical, not weighted — showing it as points would
                    // make the total look wrong.
                    <Badge variant="danger">Forces {f.floor}</Badge>
                  )}
                  <span className="font-semibold">+{f.points}</span>
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-600">{f.reason}</p>
            </div>
          ))}
          {profile.factors.length === 0 && (
            <p className="text-sm text-gray-500">No factors recorded.</p>
          )}
        </div>
      </Card>

      {profile.overrideReason && (
        <Card>
          <h2 className="mb-1 text-sm font-semibold">Manual override</h2>
          <p className="text-sm text-gray-600">{profile.overrideReason}</p>
          <p className="mt-1 text-xs text-gray-500">
            The computed factors above are what the system concluded before this
            override, kept so the two can be compared.
          </p>
        </Card>
      )}

      {can('compliance.close') && (
        <Card>
          <h2 className="mb-2 text-sm font-semibold">Override the rating</h2>
          <p className="mb-2 text-xs text-gray-500">
            This sets the review cadence and the depth of due diligence, so the
            reason is the record an examiner reads.
          </p>
          <div className="space-y-2">
            <select
              className="rounded border px-2 py-1 text-sm"
              value={rating}
              onChange={(e) => setRating(e.target.value as 'low' | 'medium' | 'high')}
            >
              <option value="">Select a rating…</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <textarea
              className="w-full rounded border px-2 py-1"
              rows={3}
              placeholder="Why is this the right rating, given what the system concluded?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="text-xs text-gray-500">
              {reason.trim().length}/{MIN_REASON} characters minimum
            </div>
            <Button disabled={busy || !rating || reason.trim().length < MIN_REASON} onClick={override}>
              {busy ? 'Saving…' : 'Override'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
